// Top 15 High Spenders — customers ranked by net revenue collected at
// the branch. Defaults to All Time; date-scoping counts payments and
// refunds inside the window only.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

export default function TopSpendersReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("all_time");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/top-spenders`, { params: { ...range, limit: 15 } });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw numbers; refunds signed negative.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const signedMoney = (v) => (v < 0 ? `- ${money(Math.abs(v))}` : money(v));
    const num = { numeric: true, format: money, align: "right" };
    const signedNum = { numeric: true, format: signedMoney, align: "right" };
    const rows = (data.rows || []).map((r) => ({
      rank: `#${r.rank}`,
      customer: r.customer_name,
      orders: r.orders,
      gross: Number(r.gross) || 0,
      refunds: r.refunds ? -Math.abs(Number(r.refunds)) : 0,
      net: Number(r.net) || 0,
    }));
    return {
      filename: "top-15-high-spenders",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns: [
        { header: "Rank", key: "rank" },
        { header: "Customer", key: "customer" },
        { header: "Orders", key: "orders", align: "right" },
        { header: "Gross", key: "gross", ...num },
        { header: "Refunds", key: "refunds", ...signedNum },
        { header: "Net", key: "net", ...num },
      ],
      rows,
      footerRow: ["", "Top 15 · Total Net", "", "", "", Number(data.total_net) || 0],
    };
  }, [data, branch]);

  return (
    <ReportShell title="Top 15 High Spenders" subtitle="Guests ranked by net revenue at this location. Payments inside the window only when a date range is set." branchName={branch?.name} tab="customers" exportSpec={exportSpec}>
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <Card className="rounded-lg overflow-hidden" data-testid="top-spenders-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-baseline justify-between">
              <div>
                <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                <h2 className="text-lg font-semibold">Top Spenders</h2>
              </div>
              {data && (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">top 15 · net</div>
                  <div className="text-xl font-semibold" data-testid="top-spenders-total">{formatBranchMoney(branch, data.total_net)}</div>
                </div>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider w-14">Rank</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Customer</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Orders</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Gross</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Refunds</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data || data.count === 0) ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                    {loading ? "Loading…" : "No qualifying customers."}
                  </TableCell></TableRow>
                ) : data.rows.map((r) => (
                  <TableRow key={r.customer_id} data-testid={`top-spender-row-${r.rank}`}>
                    <TableCell className="text-sm font-semibold">#{r.rank}</TableCell>
                    <TableCell className="text-sm">
                      <Link to={`/staff/brand/customers/${r.customer_id}`} className="text-primary hover:underline">{r.customer_name}</Link>
                    </TableCell>
                    <TableCell className="text-right text-sm">{r.orders}</TableCell>
                    <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.gross)}</TableCell>
                    <TableCell className="text-right text-sm">{r.refunds ? `- ${formatBranchMoney(branch, r.refunds)}` : formatBranchMoney(branch, 0)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatBranchMoney(branch, r.net)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
